import React, { useState, useRef } from 'react';
import { Stack, Typography, Box } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import JSZip from 'jszip';
import {
  PyrenzModal,
  PyrenzModalContent,
  PyrenzBlueButtonWithLoading,
  PyrenzDropzone,
} from '@client/theme';
import { Utils } from '@client/utility';
import { GetUserData, Textarea } from '../../../..';
import { usePyrenzAlert } from '@renderer/provider';

interface CreateThemeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onThemeCreated?: (theme: any) => void;
}

export function CreateThemeModal({
  isOpen,
  onClose,
  onThemeCreated,
}: CreateThemeModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [newTheme, setNewTheme] = useState({
    theme_name: '',
    theme_description: '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const alert = usePyrenzAlert();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/zip': ['.zip'] },
    maxFiles: 1,
    maxSize: 2 * 1024 * 1024,
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const rejected = rejectedFiles[0];
        if (!rejected.file.name.endsWith('.zip'))
          alert('Only .zip files are allowed', 'alert');
        else if (rejected.file.size > 2 * 1024 * 1024)
          alert('File size exceeds 2MB', 'alert');
        return;
      }

      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);

      try {
        const zip = await JSZip.loadAsync(selectedFile);
        const allowedExtensions = ['.json', '.css'];
        for (const fileName of Object.keys(zip.files)) {
          const ext = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
          if (!allowedExtensions.includes(ext)) {
            alert(`Zip contains invalid file: ${fileName}`, 'error');
            setFile(null);
            return;
          }
        }

        const regFile =
          zip.file('registration.json') ||
          Object.values(zip.files).find((f) =>
            f.name.endsWith('registration.json')
          );
        if (!regFile) {
          alert('registration.json is missing', 'error');
          setFile(null);
          return;
        }

        const content = await regFile.async('string');
        const json = JSON.parse(content);

        if (
          typeof json.id !== 'string' ||
          typeof json.name !== 'string' ||
          typeof json.description !== 'string' ||
          typeof json.creator !== 'string' ||
          json.type !== 'theme' ||
          !Array.isArray(json.themePath) ||
          !Array.isArray(json.include)
        ) {
          alert('registration.json is invalid', 'error');
          setFile(null);
          return;
        }

        const invalidThemePath = json.themePath.some(
          (p: string) => !p.endsWith('.json')
        );
        const invalidIncludes = json.include.some(
          (p: string) => !p.endsWith('.css')
        );
        if (invalidThemePath || invalidIncludes) {
          alert('registration.json paths or includes are invalid', 'error');
          setFile(null);
          return;
        }

        setNewTheme((prev) => ({
          ...prev,
          theme_name: json.name,
          theme_description: json.description,
        }));
      } catch (err) {
        alert('Failed to read or validate registration.json', 'error');
        setFile(null);
      }
    },
  });

  const handleChange = (key: string, value: string) => {
    setNewTheme((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!file) {
      alert('Please select a valid .zip file', 'alert');
      return;
    }
    setSubmitting(true);
    try {
      const userData = await GetUserData();
      const theme_creator = userData?.username || 'Unknown';

      const filePath = `user_themes/${file.name}`;
      const { error: uploadError } = await Utils.db.client.storage
        .from('themes')
        .upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: publicUrlData } = Utils.db.client.storage
        .from('themes')
        .getPublicUrl(filePath);
      const theme_link = publicUrlData?.publicUrl || '';

      const inserted = await Utils.db.insert({
        tables: 'themes',
        data: { ...newTheme, theme_creator, theme_link },
      });

      if (!inserted || inserted.length === 0)
        throw new Error('Failed to insert theme');

      if (onThemeCreated)
        onThemeCreated({ ...newTheme, theme_creator, theme_link });
      setNewTheme({ theme_name: '', theme_description: '' });
      setFile(null);
      onClose();
    } catch (e: any) {
      console.error(e);
      alert(e?.message || 'Failed to create theme', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PyrenzModal open={isOpen} onClose={onClose}>
      <PyrenzModalContent ref={modalRef}>
        <Stack spacing={2} mt={1}>
          <PyrenzDropzone {...getRootProps()} sx={{ textAlign: 'center' }}>
            <input {...getInputProps()} />
            {file ? (
              <Typography>{file.name}</Typography>
            ) : isDragActive ? (
              <Typography>Drop the .zip file here...</Typography>
            ) : (
              <Typography>
                Drag & drop a .zip file here, or click to select file
              </Typography>
            )}
          </PyrenzDropzone>

          <Textarea
            label="Theme Name"
            value={newTheme.theme_name}
            onChange={(e) => handleChange('theme_name', e.target.value)}
          />
          <Textarea
            label="Theme Description"
            value={newTheme.theme_description}
            onChange={(e) => handleChange('theme_description', e.target.value)}
          />

          <Box display="flex" justifyContent="center" mt={2}>
            <PyrenzBlueButtonWithLoading
              dataState={submitting ? 'loading' : undefined}
              onClick={handleSubmit}
            >
              Submit
            </PyrenzBlueButtonWithLoading>
          </Box>
        </Stack>
      </PyrenzModalContent>
    </PyrenzModal>
  );
}
